// The client of the analytics API (R-AN2): the catalog of metrics the person may ask for and one query; both count on the server.

import { useEffect, useState } from 'react';
import type { Id } from '@confdeck/core-shared';

export type MetricLevel = 'account' | 'parent' | 'scope';
export interface CatalogMetric { key: string; name: string; description: string; basis: string; unit: 'count' | 'money'; levels: MetricLevel[]; app: string | null; breakdowns: { key: string; label: string }[]; filter: boolean }
export interface Catalog {
  levels: { account: string; parent: string | null; scope: string | null };
  types: { parent: Id | null; scope: Id | null };
  limits: { days: number; points: number };
  metrics: CatalogMetric[];
}
export type Period = { from: string; to: string } | 'all';
export interface MetricQuery { metric: string; scope: { recordId: Id } | null; period: Period; compare?: 'previous'; breakdown?: string }
export interface MetricAnswer { metric: string; unit: 'count' | 'money'; value: number; previous?: { from: string; to: string; value: number }; breakdown?: { key: string; label: string; value: number }[] }

/** The catalog once per page: the metrics do not change while the person looks. */
export function useCatalog(): { catalog: Catalog | null; error: string | null } {
  const [state, setState] = useState<{ catalog: Catalog | null; error: string | null }>({ catalog: null, error: null });
  useEffect(() => {
    let live = true;
    fetch('/api/analytics/metrics', { credentials: 'same-origin' })
      .then(async (r) => { const body = await r.json(); if (!r.ok) throw new Error(body?.error?.message ?? 'Каталог чисел недоступен'); return body as Catalog; })
      .then((catalog) => { if (live) setState({ catalog, error: null }); })
      .catch((e: Error) => { if (live) setState({ catalog: null, error: e.message }); });
    return () => { live = false; };
  }, []);
  return state;
}

export async function queryMetric(q: MetricQuery): Promise<{ ok: true; answer: MetricAnswer } | { ok: false; message: string }> {
  try {
    const r = await fetch('/api/analytics/query', { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify(q) });
    const body = await r.json();
    return r.ok ? { ok: true, answer: body as MetricAnswer } : { ok: false, message: body?.error?.message ?? 'Число не посчитано' };
  } catch {
    return { ok: false, message: 'Нет связи с сервером' };
  }
}

/** One number, asked again when the query changes; a refusal comes with its reason. */
export function useMetric(q: MetricQuery | null): { answer: MetricAnswer | null; error: string | null; loading: boolean } {
  const key = q ? JSON.stringify(q) : '';
  const [state, setState] = useState<{ key: string; answer: MetricAnswer | null; error: string | null }>({ key: '', answer: null, error: null });
  useEffect(() => {
    if (!q) return;
    let live = true;
    void queryMetric(q).then((r) => { if (live) setState(r.ok ? { key, answer: r.answer, error: null } : { key, answer: null, error: r.message }); });
    return () => { live = false; };
  }, [key]);
  return { answer: state.key === key ? state.answer : null, error: state.key === key ? state.error : null, loading: !!q && state.key !== key };
}
